import React, { useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { formatDuration } from "../../../utils/attendance";
import { AGENT_INK, AGENT_LINE, AGENT_MUTED } from "../../../agentComponents/agentTheme";

// Effective hours per day for the last seven days against the daily target,
// the way Keka shows the week: one bar per day, filled to the hours worked,
// with the target as a dashed line across the chart.

const BAR_AREA = 128;

const dayKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(
    x.getDate()
  ).padStart(2, "0")}`;
};

export default function WeeklyHours({ shifts, targetHours = 8 }) {
  const days = useMemo(() => {
    // Seven buckets ending today, so the chart reads left-to-right into "now".
    const buckets = [];
    for (let i = 6; i >= 0; i -= 1) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      buckets.push({
        key: dayKey(d),
        label: d.toLocaleDateString([], { weekday: "short" }).slice(0, 3),
        dateNum: d.getDate(),
        isToday: i === 0,
        productiveMs: 0,
        breakMs: 0,
        shifts: 0,
      });
    }
    const byKey = new Map(buckets.map((b) => [b.key, b]));

    for (const s of shifts) {
      // `date` is the server's business day, which is the bucket that counts.
      const bucket = byKey.get(s.date);
      if (!bucket) continue;
      bucket.productiveMs += s.totals.productiveMs;
      bucket.breakMs += s.totals.breakMs;
      bucket.shifts += 1;
    }
    return buckets;
  }, [shifts]);

  const targetMs = targetHours * 3600000;
  const peak = Math.max(targetMs, ...days.map((d) => d.productiveMs)) || targetMs;
  const targetLine = (targetMs / peak) * BAR_AREA;

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: `1px solid ${AGENT_LINE}`, p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: AGENT_INK }}>Last 7 Days</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 16, borderTop: "2px dashed #CBD5E1" }} />
          <Typography sx={{ fontSize: 11.5, color: AGENT_MUTED }}>{targetHours}h target</Typography>
        </Box>
      </Box>

      <Box sx={{ position: "relative", height: BAR_AREA }}>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: targetLine,
            borderTop: "2px dashed #CBD5E1",
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            display: "flex",
            alignItems: "flex-end",
            gap: { xs: 1, sm: 1.5 },
          }}
        >
          {days.map((d) => {
            const met = d.productiveMs >= targetMs;
            // A day with no shift draws nothing. A stub bar there reads as an
            // underline beneath the weekday label rather than as "no data".
            const height = d.shifts ? Math.max(8, (d.productiveMs / peak) * BAR_AREA) : 0;
            return (
              <Tooltip
                key={d.key}
                arrow
                title={
                  d.shifts
                    ? `${formatDuration(d.productiveMs)} effective · ${formatDuration(d.breakMs)} break`
                    : "No shift logged"
                }
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    height: "100%",
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      height,
                      borderRadius: "6px 6px 0 0",
                      bgcolor: met ? "#16A34A" : "#FBBF24",
                      transition: "height .4s ease",
                      "&:hover": { filter: "brightness(1.07)" },
                    }}
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* Baseline the bars sit on, so an empty day reads as zero rather than
          as a missing element. */}
      <Box sx={{ height: "1px", bgcolor: AGENT_LINE }} />

      <Box sx={{ display: "flex", gap: { xs: 1, sm: 1.5 }, pt: 1 }}>
        {days.map((d) => (
          <Box key={d.key} sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: d.isToday ? 700 : 500,
                color: d.isToday ? AGENT_INK : AGENT_MUTED,
              }}
              noWrap
            >
              {d.label}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: "#94A3B8" }}>{d.dateNum}</Typography>
            {/* Today marker: a dot under the label, rather than an outline on
                the bar -- on a short bar an outline looks like a stray pill. */}
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                mx: "auto",
                mt: 0.5,
                bgcolor: d.isToday ? "#0C237B" : "transparent",
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
